import { Flex } from 'front-commons/ds/components/Flex';
import { Suspense, lazy, useMemo } from 'react';
import { AlternativeNavigationBanner } from './components/AlternativeNavigationBanner';
import { HeaderBottomRow } from './components/BottomRow';
import { CategoriesMobile } from './components/BottomRow/components/HeaderNav/components/Categories/variants/Mobile';
import { PosSelectorDrawer } from './components/TopRow/components/PosSelector/variants/DrawerPosSelector/components/PosSelectorDrawer';
import { HeaderContext } from './context';
import { useHeaderState } from './hooks/useHeaderState';
import type { HeaderProps } from './interfaces';

export { HEADER_HEIGHT_CSS_VAR } from './constants';

const HeaderTopRow = lazy(() => import('./components/TopRow').then(({ HeaderTopRow }) => ({ default: HeaderTopRow })));

export function Header({
	basketData,
	catalogMenuItems,
	useGetCategoriesPh,
	customerStore,
	fidelityControls,
	isRetryFetchDataExpired,
	isSuggestionsProductNotifierExpired,
	loadingBasket,
	loadingPos,
	posStore,
	logoButtonProps,
	selectedPos,
	stickyTopRow = false,
	type = 'logged',
	userMenu,
	showHelpButton,
	orderImportControls,
	getSearchPreview,
	getUsePosSearch,
	handleOpenBasket,
	handleClearRetryFetchData,
	alternativeNavigationBanner,
	handleSelectPos,
	handleSetFidelityData,
	mainCustomerPage,
	searchEventGTM,
	selectContentGTM,
	simulatedViewType,
}: HeaderProps) {
	const {
		showCategories,
		setShowCategories,
		location,
		headerRef,
		posSelector,
		setPosSelector,
		isTopRowVisible,
		handleTopRowVisibilityChange,
		isHomeOrDynamicWithPharmacy,
		isValidCustomer,
		showBrandSectionMenu,
		showBasketButton,
		isHelpPage,
	} = useHeaderState({ type, stickyTopRow, posStore, customerStore });

	const posId = posStore.selectedPos?.id || '';

	const contextMemoizedValue = useMemo(
		() => ({
			pharmacy: {
				posStore,
				selectedPos,
				loadingPos,
				getUsePosSearch,
				handleSelectPos,
				mainCustomerPage,
				simulatedViewType,
				posSelector,
				setPosSelector,
			},
			categories: { useGetCategoriesPh, posId, setShowCategories },
			fidelity: fidelityControls,
			profile: userMenu,
			tracking: { selectContentGTM, searchEventGTM },
			orderImport: orderImportControls,
			search: { getSearchPreview },
		}),
		[
			posSelector,
			setPosSelector,
			useGetCategoriesPh,
			posId,
			setShowCategories,
			fidelityControls,
			userMenu,
			selectContentGTM,
			searchEventGTM,
			orderImportControls,
			posStore,
			selectedPos,
			loadingPos,
			getUsePosSearch,
			handleSelectPos,
			mainCustomerPage,
			simulatedViewType,
			getSearchPreview,
		],
	);

	return (
		<HeaderContext.Provider value={contextMemoizedValue}>
			<Flex
				as="header"
				display="block"
				gap="normal"
				position="sticky"
				raw={{ zIndex: '101 !important' }}
				ref={headerRef}
			>
				{alternativeNavigationBanner && <AlternativeNavigationBanner {...alternativeNavigationBanner} />}

				<Suspense fallback={<div />}>
					<HeaderTopRow
						handleClearRetryFetchData={handleClearRetryFetchData}
						handleSetFidelityData={handleSetFidelityData}
						isRetryFetchDataExpired={isRetryFetchDataExpired}
						location={location}
						logoButtonProps={logoButtonProps}
						onVisibilityChange={handleTopRowVisibilityChange}
						showMenu={isValidCustomer && showBrandSectionMenu}
						showPosSelector={isHomeOrDynamicWithPharmacy}
						showSearch={isHomeOrDynamicWithPharmacy}
						stickyTopRow={stickyTopRow}
						showHelpButton={showHelpButton && !isHelpPage}
					/>
				</Suspense>

				<CategoriesMobile open={showCategories} onClose={() => setShowCategories(false)} />

				{isHomeOrDynamicWithPharmacy && (
					<HeaderBottomRow
						basketData={basketData}
						catalogMenuItems={catalogMenuItems}
						isSuggestionsProductNotifierExpired={isSuggestionsProductNotifierExpired}
						isTopRowVisible={stickyTopRow ? isTopRowVisible : true}
						loadingBasket={loadingBasket}
						showBasketButton={showBasketButton}
						type={type}
						handleOpenBasket={handleOpenBasket}
					/>
				)}
			</Flex>

			{posSelector?.expanded && <PosSelectorDrawer />}
		</HeaderContext.Provider>
	);
}
